import caseService from "./../../services/CaseService";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { Case } from "../../interfaces/Case";

interface CaseState {
  cases: Case[];
  singleCase?: Case;
  error?: string | null;
  loading: boolean;
}

const initialState: CaseState = {
  cases: [],
  singleCase: {} as Case,
  error: "",
  loading: false,
};

export const fetchCases = createAsyncThunk(
  "case/fetchCases",
  async (params: string, { rejectWithValue }) => {
    try {
      const response = await caseService.getAll(params);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);
export const fetchCasesBySuiteId = createAsyncThunk(
  "case/fetchCasesBySuiteId",
  async (suiteId: number) => {
    try {
      const response = await caseService.getBySuiteId(suiteId);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);
export const fetchCasesByAgentId = createAsyncThunk(
  "case/fetchCasesByAgentId",
  async (agentId: string) => {
    try {
      const response = await caseService.getByAgentId(agentId);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const addCase = createAsyncThunk("case/addCase", async (data: any) => {
  try {
    const response = await caseService.post(data);
    return response.data;
  } catch (error) {
    return error;
  }
});

export const updateCase = createAsyncThunk(
  "case/updateCase",
  async (data: any) => {
    try {
      const response = await caseService.put(data.id, {
        title: data.title,
        tc_result: data.tc_result,
        sys_result: data.sys_result,
        tool_data: data.tool_data,
        manifest: data.manifest,
        meta_data: data.meta_data,
        original_path: data.original_path,
        file_name: data.file_name,
        suite_id: data.suite_id,
      });
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const removeCase = createAsyncThunk(
  "case/removeCase",
  async (id: number) => {
    try {
      const response = await caseService.remove(id);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const getCaseById = createAsyncThunk(
  "case/getById",
  async (id: number) => {
    try {
      const response = await caseService.get(id);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

const caseSlice = createSlice({
  name: "case",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchCases.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchCases.fulfilled, (state, action) => {
      state.loading = false;
      state.cases = action.payload.cases;
    });
    builder.addCase(fetchCases.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    builder.addCase(addCase.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addCase.fulfilled, (state, action) => {
      state.loading = false;
      state.cases.push(action.payload);
    });
    builder.addCase(addCase.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    builder.addCase(updateCase.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateCase.fulfilled, (state, action) => {
      state.loading = false;
      state.cases = state.cases.map((el) =>
        el.id === action.payload.id ? action.payload : el
      );
    });
    builder.addCase(updateCase.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    builder.addCase(getCaseById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCaseById.fulfilled, (state, action) => {
      state.loading = false;
      state.singleCase = action.payload;
    });
    builder.addCase(getCaseById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    builder.addCase(removeCase.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(removeCase.fulfilled, (state, action) => {
      state.loading = false;
      state.cases = state.cases.filter((el) => el.id !== action.payload.id);
    });
    builder.addCase(removeCase.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
  },
});

const { reducer } = caseSlice;
export default reducer;
